import { z } from "zod";
import { boundingRect, buildParentMap, type Rect, worldBox } from "../scene/geometry";
import { pageSize } from "../scene/pages";
import type { SceneDocument } from "../scene/schema";

/**
 * Positions the way a person says them ("below the headline", "bottom right,
 * 5 % from the edge", "upper third") – resolved to pixels on the server, so
 * an AI never has to calculate coordinates (docs/23-mcp-v2.md, 1.3).
 */

export const ANCHORS = [
  "top-left",
  "top",
  "top-right",
  "left",
  "center",
  "right",
  "bottom-left",
  "bottom",
  "bottom-right",
] as const;

export const ZONES = [
  "top-third",
  "middle-third",
  "bottom-third",
  "left-half",
  "right-half",
  "top-half",
  "bottom-half",
] as const;

export const placementSchema = z
  .object({
    x: z.number().optional().describe("Linke Kante (von der Seite bzw. dem Bereich aus)"),
    y: z.number().optional().describe("Obere Kante"),
    w: z.number().positive().optional().describe("Breite"),
    h: z.number().positive().optional().describe("Höhe"),
    unit: z
      .enum(["px", "mm", "%"])
      .default("px")
      .describe("Einheit von x, y, w, h, gap, margin (% der Seitenbreite bzw. -höhe)"),
    below: z.string().optional().describe("Unter dieses Element (Nummer)"),
    above: z.string().optional().describe("Über dieses Element"),
    leftOf: z.string().optional().describe("Links neben dieses Element"),
    rightOf: z.string().optional().describe("Rechts neben dieses Element"),
    gap: z.number().optional().describe("Abstand zum Bezugselement (Standard ca. 2 % der Seite)"),
    align: z
      .enum(["left", "center", "right"])
      .optional()
      .describe("Horizontal im Bereich bzw. zum Bezugselement"),
    valign: z.enum(["top", "middle", "bottom"]).optional().describe("Vertikal"),
    anchor: z.enum(ANCHORS).optional().describe("Ecke, Kante oder Mitte des Bereichs"),
    margin: z
      .union([z.number(), z.literal("safe")])
      .optional()
      .describe(
        "Abstand zum Rand des Bereichs; safe = Sicherheitsabstand bzw. ca. 5,5 % der Seite",
      ),
    within: z.string().optional().describe("Bereich = Box dieses Elements statt der Seite"),
    zone: z.enum(ZONES).optional().describe("Bereich = Drittel bzw. Hälfte der Seite"),
  })
  .strict();
export type Placement = z.infer<typeof placementSchema>;
export type PlacementInput = z.input<typeof placementSchema>;

export interface PlaceContext {
  doc: SceneDocument;
  pageIndex: number;
  /** Element id for a ref (or id); null when unknown. */
  resolve: (key: string) => string | null;
}

export interface PlacedBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const round = (value: number) => Math.round(value * 10) / 10;

export class PlacementError extends Error {}

/** The element a placement refers to, as a page-space rect. */
function referenced(
  context: PlaceContext,
  key: string,
  parents = buildParentMap(context.doc),
): Rect {
  const id = context.resolve(key);
  if (!id) throw new PlacementError(`Element „${key}“ gibt es nicht (Bezug der Position)`);
  const box = worldBox(context.doc, id, parents);
  if (!box) throw new PlacementError(`Element „${key}“ gibt es nicht (Bezug der Position)`);
  return boundingRect(box);
}

/**
 * The box for a placement. `current` is the size the element has now (or will
 * get); `w`/`h` override it. Without any position the element keeps `current`'s
 * x and y when given, else it is centred.
 */
export function resolvePlacement(
  context: PlaceContext,
  spec: PlacementInput,
  current: { width: number; height: number; x?: number; y?: number },
): PlacedBox {
  const { doc, pageIndex } = context;
  const page = doc.pages[pageIndex];
  if (!page) throw new PlacementError(`Seite ${pageIndex + 1} gibt es nicht`);
  const size = pageSize(doc, page);
  const short = Math.min(size.width, size.height);
  const print = doc.format.print;
  const pxPerMm = print ? doc.format.width / print.widthMm : 96 / 25.4;
  const unit = spec.unit ?? "px";
  const ux = (value: number) =>
    unit === "%" ? (value / 100) * size.width : unit === "mm" ? value * pxPerMm : value;
  const uy = (value: number) =>
    unit === "%" ? (value / 100) * size.height : unit === "mm" ? value * pxPerMm : value;
  const us = (value: number) =>
    unit === "%" ? (value / 100) * short : unit === "mm" ? value * pxPerMm : value;
  const parents = buildParentMap(doc);

  const width = spec.w !== undefined ? ux(spec.w) : current.width;
  const height = spec.h !== undefined ? uy(spec.h) : current.height;

  // The area the element is placed in.
  let area: Rect = spec.within
    ? referenced(context, spec.within, parents)
    : { x: 0, y: 0, width: size.width, height: size.height };
  if (spec.zone) {
    const { x, y, width: aw, height: ah } = area;
    area = {
      "top-third": { x, y, width: aw, height: ah / 3 },
      "middle-third": { x, y: y + ah / 3, width: aw, height: ah / 3 },
      "bottom-third": { x, y: y + (ah * 2) / 3, width: aw, height: ah / 3 },
      "left-half": { x, y, width: aw / 2, height: ah },
      "right-half": { x: x + aw / 2, y, width: aw / 2, height: ah },
      "top-half": { x, y, width: aw, height: ah / 2 },
      "bottom-half": { x, y: y + ah / 2, width: aw, height: ah / 2 },
    }[spec.zone];
  }
  const margin =
    spec.margin === "safe"
      ? print
        ? print.safeMm * pxPerMm
        : short * 0.055
      : spec.margin !== undefined
        ? us(spec.margin)
        : 0;
  const inner: Rect = {
    x: area.x + margin,
    y: area.y + margin,
    width: Math.max(1, area.width - margin * 2),
    height: Math.max(1, area.height - margin * 2),
  };

  const gap = spec.gap !== undefined ? us(spec.gap) : Math.round(short * 0.02);
  let x: number | undefined;
  let y: number | undefined;

  const horizontalIn = (rect: Rect, align: "left" | "center" | "right") =>
    align === "left"
      ? rect.x
      : align === "center"
        ? rect.x + (rect.width - width) / 2
        : rect.x + rect.width - width;
  const verticalIn = (rect: Rect, align: "top" | "middle" | "bottom") =>
    align === "top"
      ? rect.y
      : align === "middle"
        ? rect.y + (rect.height - height) / 2
        : rect.y + rect.height - height;

  if (spec.below || spec.above) {
    const ref = referenced(context, (spec.below ?? spec.above)!, parents);
    y = spec.below ? ref.y + ref.height + gap : ref.y - gap - height;
    x = horizontalIn(ref, spec.align ?? "left");
  } else if (spec.rightOf || spec.leftOf) {
    const ref = referenced(context, (spec.rightOf ?? spec.leftOf)!, parents);
    x = spec.rightOf ? ref.x + ref.width + gap : ref.x - gap - width;
    y = verticalIn(ref, spec.valign ?? "top");
  } else {
    const [vertical, horizontal] = spec.anchor
      ? ([
          spec.anchor.startsWith("top")
            ? "top"
            : spec.anchor.startsWith("bottom")
              ? "bottom"
              : "middle",
          spec.anchor.endsWith("left") || spec.anchor === "left"
            ? "left"
            : spec.anchor.endsWith("right") || spec.anchor === "right"
              ? "right"
              : "center",
        ] as const)
      : ([spec.valign, spec.align] as const);
    if (horizontal) x = horizontalIn(inner, horizontal);
    if (vertical) y = verticalIn(inner, vertical);
  }
  // Explicit coordinates win on their axis (measured from the area).
  if (spec.x !== undefined) x = area.x + ux(spec.x);
  if (spec.y !== undefined) y = area.y + uy(spec.y);
  // Nothing said: stay, or centre.
  x ??= current.x ?? inner.x + (inner.width - width) / 2;
  y ??= current.y ?? inner.y + (inner.height - height) / 2;
  return { x: round(x), y: round(y), width: round(width), height: round(height) };
}
